import { Property } from '../property';
import { PropertyAssignment } from './models/property-assignment';

export type VehicleFieldRole = 'vin' | 'make' | 'model' | 'year' | 'trim';

/** Condition paths (e.g. `roadVehicle.roadVehicleMake`) of the fields holding a vehicle's VIN and make. */
export interface VehicleFieldPaths {
  vin: string | null;
  make: string | null;
}

const ROAD_VEHICLE_FORM_ID = 'road_vehicle';

/** Which vehicle detail a field holds, judged by its label, property name or value key. */
export function getVehicleFieldRole(labels: Array<string | null | undefined>): VehicleFieldRole | null {
  const values = labels
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map(value => value.trim().toLowerCase());

  if (values.some(value => value === 'vin' || value.endsWith('vin'))) return 'vin';
  // The word "make" anywhere also counts, so a combined "Make/Model" field holds the make.
  if (values.some(value => value.endsWith('make') || /\bmake\b/.test(value))) return 'make';
  if (values.some(value => value === 'model' || value.endsWith('model'))) return 'model';
  if (values.some(value => value === 'year' || value.endsWith('year') || value === 'model year')) return 'year';
  if (values.some(value => value === 'trim' || value.endsWith('trim'))) return 'trim';

  return null;
}

/**
 * Finds where an asset type keeps its VIN and make. A Road Vehicle field wins, since that's where
 * the VIN lookup fills the make in; otherwise top-level fields are checked with the same rules.
 */
export function getVehicleFieldPaths(propertySet: { properties?: PropertyAssignment[] }): VehicleFieldPaths {
  const assignments = (propertySet.properties ?? []).filter(assignment => assignment.property && !assignment.hidden);

  for (const assignment of assignments) {
    const property = assignment.property as Property.Model<Property.PropertyType>;
    if (property.type !== Property.PropertyType.OBJECT || property.formId !== ROAD_VEHICLE_FORM_ID) continue;

    const children = ((property as Property.ObjectModel).properties ?? [])
      .filter(child => child.property && !child.hidden)
      .map(child => ({
        labels: [child.displayLabel, child.property?.name, child.property?.valueKey],
        path: `${property.valueKey}.${child.property?.valueKey}`,
      }));

    const paths = findVehicleFieldPaths(children);
    if (paths.vin || paths.make) return paths;
  }

  return findVehicleFieldPaths(
    assignments
      .filter(assignment => assignment.property?.type !== Property.PropertyType.OBJECT)
      .map(assignment => ({
        labels: [assignment.property?.name, assignment.property?.valueKey],
        path: assignment.property?.valueKey as string,
      }))
  );
}

function findVehicleFieldPaths(fields: { labels: Array<string | null | undefined>; path: string }[]) {
  const paths: VehicleFieldPaths = { vin: null, make: null };

  for (const field of fields) {
    const role = getVehicleFieldRole(field.labels);
    if (role === 'vin' && !paths.vin) paths.vin = field.path;
    if (role === 'make' && !paths.make) paths.make = field.path;
  }

  return paths;
}
